import {computed, inject, Injectable, signal} from '@angular/core';
import {Router} from '@angular/router';
import {QueryClient} from '@tanstack/angular-query-experimental';
import {ImageCacheService} from '../services/images-cache/images-cache.service';

@Injectable({providedIn: 'root'})
export class TokenService {

  private readonly router = inject(Router);
  private readonly queryClient = inject(QueryClient);
  private readonly imageCacheService = inject(ImageCacheService);
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  readonly token = signal<string | null>(localStorage.getItem(this.TOKEN_KEY));
  readonly refreshToken = signal<string | null>(localStorage.getItem(this.REFRESH_TOKEN_KEY));
  readonly isAuthenticated = computed(() => {
    // A refresh token means the session is still valid — the interceptor refreshes the JWT transparently
    if (this.refreshToken()) return true;
    const t = this.token();
    if (!t) return false;
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      return !payload.exp || payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  });

  login(token: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    this.token.set(token);
    this.refreshToken.set(refreshToken);
  }

  updateTokens(token: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    this.token.set(token);
    this.refreshToken.set(refreshToken);
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.token.set(null);
    this.refreshToken.set(null);
    this.queryClient.clear();
    this.imageCacheService.clear();
    this.router.navigate(['/auth']);
  }
}
